/**
 * 📱 Map ↔ Viewport coordinate helpers for mobile portrait mode.
 *
 * On mobile portrait the world map is rendered at 200% of the viewport
 * width and the user can swipe-pan horizontally. All `MapView2D` country
 * positions (e.g. USA at x:20%) are expressed in *map* coordinates.
 *
 * Animations like AirStrikeAnimation render inside a `fixed inset-0`
 * overlay, which is sized to the *viewport* (100% width). So a bomber at
 * x:20% lands at 20% of the viewport instead of 20% of the wider map —
 * which on a phone means USA appears far off the left edge.
 *
 * These helpers:
 *   1. Pre-scroll the map so source + target are both on-screen.
 *   2. Translate map-coordinate positions to viewport-relative positions
 *      so animations using `fixed` layout line up with the rendered map.
 *
 * Desktop and landscape phones aren't affected — the map is already
 * viewport-sized so map% == viewport% and these helpers return the
 * input unchanged.
 */

// Keep in sync with MapView2D: on mobile portrait we push the map down
// to give crowded northern countries breathing room. Animations rendered
// with `position: fixed` need to compensate or bombs drop in the sky.
const MOBILE_PORTRAIT_TOP_OFFSET_PX = 80;

const isMobilePortrait = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768 && window.innerHeight > window.innerWidth;
};

const getScrollContainer = () => {
  if (typeof document === 'undefined') return null;
  return document.querySelector('[data-map-scroll-container]');
};

/**
 * Pre-scroll the map and remap source/target from map-space to viewport-
 * space so a `fixed`-positioned animation overlays the actual map.
 *
 * Strategy:
 *   - Always center the TARGET in the viewport. The explosion is the
 *     dramatic centerpiece and must be on screen.
 *   - The bomber may take off from off-screen if source+target are too
 *     far apart to both fit, which is fine — the user's eye goes to the
 *     target.
 *   - Vertically, shift positions DOWN by the map's top offset so bombs
 *     land on the country (not above it in the sky band).
 *
 * On desktop or landscape, returns the positions unchanged.
 */
export const prepareAirStrikeViewport = (sourcePos, targetPos) => {
  if (!isMobilePortrait()) {
    return { sourcePos, targetPos };
  }

  const el = getScrollContainer();
  if (!el || el.scrollWidth <= el.clientWidth) {
    return { sourcePos, targetPos };
  }

  const mapWidth = el.scrollWidth;     // ~ 2 * clientWidth on mobile portrait
  const viewportWidth = el.clientWidth;
  const viewportHeight = window.innerHeight;

  // Center the TARGET in the viewport (where the explosion happens).
  // Clamp so we don't scroll past either edge of the map.
  const targetMapPixel = (targetPos.x / 100) * mapWidth;
  const targetScrollLeft = Math.max(
    0,
    Math.min(targetMapPixel - viewportWidth / 2, mapWidth - viewportWidth)
  );

  // Snap-scroll synchronously so the remap math below stays consistent.
  el.scrollLeft = targetScrollLeft;

  const mapXToViewportX = (mapPercent) => {
    const mapPixel = (mapPercent / 100) * mapWidth;
    return ((mapPixel - targetScrollLeft) / viewportWidth) * 100;
  };

  // Vertical compensation: the map renders 80px below the viewport top
  // on mobile portrait, but the animation overlay uses `fixed inset-0`
  // (viewport-relative). Convert 80px into a viewport-% offset and add
  // it to both endpoints so the bomb hits the country, not the sky band.
  const yOffsetPercent = (MOBILE_PORTRAIT_TOP_OFFSET_PX / viewportHeight) * 100;

  return {
    sourcePos: {
      x: mapXToViewportX(sourcePos.x),
      y: sourcePos.y + yOffsetPercent,
    },
    targetPos: {
      x: mapXToViewportX(targetPos.x),
      y: targetPos.y + yOffsetPercent,
    },
  };
};
